import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Autocomplete,
  IconButton,
  Paper,
  Tabs,
  Tab,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  Topic as TopicIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { notificationsApi, usersApi } from '../services/api';
import { User } from '../types';
import { useMultilingual } from '../hooks/useMultilingual';

interface NotificationFormData {
  targetType: 'users' | 'all' | 'topic';
  userIds: number[];
  topic: string;
  customTopic: string;
  title: string;
  titleAr: string;
  body: string;
  bodyAr: string;
  data: { [key: string]: string };
}

const Notifications: React.FC = () => {
  const { getText, isRTL } = useMultilingual();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [formData, setFormData] = useState<NotificationFormData>({
    targetType: 'users',
    userIds: [],
    topic: '',
    customTopic: '',
    title: '',
    titleAr: '',
    body: '',
    bodyAr: '',
    data: {}
  });

  const [dataKey, setDataKey] = useState('');
  const [dataValue, setDataValue] = useState('');

  useEffect(() => {
    if (open && (formData.targetType === 'users' || formData.targetType === 'all')) {
      fetchUsers();
    }
  }, [open, formData.targetType]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const allUsers = await usersApi.getAll();
      setUsers(allUsers.filter(u => u.isActive));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    setError(null);
    setSuccess(null);
    setFormData({
      targetType: 'users',
      userIds: [],
      topic: '',
      customTopic: '',
      title: '',
      titleAr: '',
      body: '',
      bodyAr: '',
      data: {}
    });
    setCurrentTab(0);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setSuccess(null);
    setFormData({
      targetType: 'users',
      userIds: [],
      topic: '',
      customTopic: '',
      title: '',
      titleAr: '',
      body: '',
      bodyAr: '',
      data: {}
    });
    setDataKey('');
    setDataValue('');
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
    const targetTypes: ('users' | 'all' | 'topic')[] = ['users', 'all', 'topic'];
    setFormData({
      ...formData,
      targetType: targetTypes[newValue],
      userIds: newValue === 0 ? formData.userIds : [],
      topic: newValue === 2 ? (formData.topic || (process.env.NODE_ENV === 'production' ? 'all_users' : 'all_users_dev')) : '',
      customTopic: newValue === 2 ? formData.customTopic : ''
    });
  };

  const handleAddData = () => {
    if (dataKey && dataValue) {
      setFormData({
        ...formData,
        data: {
          ...formData.data,
          [dataKey]: dataValue
        }
      });
      setDataKey('');
      setDataValue('');
    }
  };

  const handleRemoveData = (key: string) => {
    const newData = { ...formData.data };
    delete newData[key];
    setFormData({
      ...formData,
      data: newData
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validate form
      if (!formData.title || !formData.body) {
        setError(getText('Title and body are required', 'العنوان والنص مطلوبان'));
        setLoading(false);
        return;
      }

      if (formData.targetType === 'users' && formData.userIds.length === 0) {
        setError(getText('Please select at least one user', 'يرجى اختيار مستخدم واحد على الأقل'));
        setLoading(false);
        return;
      }

      if (formData.targetType === 'topic') {
        const selectedTopic = formData.topic === 'custom' ? formData.customTopic : formData.topic;
        if (!selectedTopic) {
          setError(getText('Topic is required', 'الموضوع مطلوب'));
          setLoading(false);
          return;
        }
      }

      // Combine English and Arabic if Arabic is provided
      const finalTitle = formData.titleAr 
        ? `${formData.title} | ${formData.titleAr}`
        : formData.title;
      
      const finalBody = formData.bodyAr
        ? `${formData.body}\n\n${formData.bodyAr}`
        : formData.body;

      let response;
      if (formData.targetType === 'users' && formData.userIds.length > 0) {
        response = await notificationsApi.sendToUsers({
          userIds: formData.userIds,
          title: finalTitle,
          body: finalBody,
          data: Object.keys(formData.data).length > 0 ? formData.data : undefined
        });
      } else if (formData.targetType === 'all') {
        response = await notificationsApi.sendToUsers({
          title: finalTitle,
          body: finalBody,
          data: Object.keys(formData.data).length > 0 ? formData.data : undefined
        });
      } else {
        const selectedTopic = formData.topic === 'custom' ? formData.customTopic : formData.topic;
        response = await notificationsApi.sendToTopic({
          topic: selectedTopic,
          title: finalTitle,
          body: finalBody,
          data: Object.keys(formData.data).length > 0 ? formData.data : undefined
        });
      }

      if (response.success) {
        setSuccess(response.message);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setError(response.message || getText('Failed to send notification', 'فشل إرسال الإشعار'));
      }
    } catch (err: any) {
      setError(err.response?.data?.message || getText('An error occurred while sending notification', 'حدث خطأ أثناء إرسال الإشعار'));
    } finally {
      setLoading(false);
    }
  };

  const selectedUsers = users.filter(u => formData.userIds.includes(u.id));

  return (
    <>
      <Button
        variant="contained"
        startIcon={<NotificationsIcon />}
        onClick={handleOpen}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #5568d3 0%, #6a4190 100%)',
          }
        }}
      >
        {getText('Send Notification', 'إرسال إشعار')}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <NotificationsIcon color="primary" />
            <Typography variant="h6">
              {getText('Send Notification', 'إرسال إشعار')}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          <Tabs value={currentTab} onChange={handleTabChange} sx={{ mb: 3 }}>
            <Tab icon={<PeopleIcon />} iconPosition="start" label={getText('Specific Users', 'مستخدمون محددون')} />
            <Tab icon={<NotificationsIcon />} iconPosition="start" label={getText('All Users', 'جميع المستخدمين')} />
            <Tab icon={<TopicIcon />} iconPosition="start" label={getText('Topic', 'موضوع')} />
          </Tabs>

          {/* Specific Users Tab */}
          {currentTab === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <Autocomplete
                  multiple
                  options={users}
                  getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                  value={selectedUsers}
                  onChange={(_, newValue) => {
                    setFormData({
                      ...formData,
                      targetType: 'users',
                      userIds: newValue.map(u => u.id)
                    });
                  }}
                  loading={loadingUsers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={getText('Select Users', 'اختر المستخدمين')}
                      placeholder={getText('Search users...', 'ابحث عن المستخدمين...')}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={`${option.firstName} ${option.lastName}`}
                        {...getTagProps({ index })}
                        key={option.id}
                      />
                    ))
                  }
                />
              </FormControl>
            </Box>
          )}

          {/* All Users Tab */}
          {currentTab === 1 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {process.env.NODE_ENV === 'production'
                ? getText(
                    'This notification will be sent to all active users via the all_users topic (production).',
                    'سيتم إرسال هذا الإشعار إلى جميع المستخدمين النشطين عبر موضوع all_users (إنتاج).'
                  )
                : getText(
                    'This notification will be sent to all active users via the all_users_dev topic (development).',
                    'سيتم إرسال هذا الإشعار إلى جميع المستخدمين النشطين عبر موضوع all_users_dev (تطوير).'
                  )}
            </Alert>
          )}

          {/* Topic Tab */}
          {currentTab === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>{getText('Select Topic', 'اختر الموضوع')}</InputLabel>
                <Select
                  value={formData.topic}
                  onChange={(e) => setFormData({ ...formData, targetType: 'topic', topic: e.target.value })}
                  label={getText('Select Topic', 'اختر الموضوع')}
                >
                  <MenuItem value="all_users">
                    {getText('All Users (Production)', 'جميع المستخدمين (إنتاج)')} - all_users
                  </MenuItem>
                  <MenuItem value="all_users_dev">
                    {getText('All Users (Dev)', 'جميع المستخدمين (تطوير)')} - all_users_dev
                  </MenuItem>
                  <MenuItem value="all_drivers">
                    {getText('All Drivers (Production)', 'جميع السائقين (إنتاج)')} - all_drivers
                  </MenuItem>
                  <MenuItem value="all_drivers_dev">
                    {getText('All Drivers (Dev)', 'جميع السائقين (تطوير)')} - all_drivers_dev
                  </MenuItem>
                  <MenuItem value="custom">
                    {getText('Custom Topic', 'موضوع مخصص')}
                  </MenuItem>
                </Select>
              </FormControl>
              
              {formData.topic === 'custom' && (
                <TextField
                  label={getText('Custom Topic Name', 'اسم الموضوع المخصص')}
                  value={formData.customTopic}
                  onChange={(e) => setFormData({ ...formData, customTopic: e.target.value })}
                  placeholder="e.g., premium_users, vip_customers"
                  fullWidth
                  required
                  helperText={getText('Enter a custom Firebase topic name', 'أدخل اسم موضوع Firebase مخصص')}
                />
              )}
              
              {formData.topic && formData.topic !== 'custom' && (
                <Alert severity="info">
                  {formData.topic === 'all_users' && getText(
                    'This notification will be sent to all users in production via the all_users topic.',
                    'سيتم إرسال هذا الإشعار إلى جميع المستخدمين في الإنتاج عبر موضوع all_users.'
                  )}
                  {formData.topic === 'all_drivers' && getText(
                    'This notification will be sent to all drivers in production via the all_drivers topic.',
                    'سيتم إرسال هذا الإشعار إلى جميع السائقين في الإنتاج عبر موضوع all_drivers.'
                  )}
                  {formData.topic === 'all_drivers_dev' && getText(
                    'This notification will be sent to all drivers in development via the all_drivers_dev topic.',
                    'سيتم إرسال هذا الإشعار إلى جميع السائقين في التطوير عبر موضوع all_drivers_dev.'
                  )}
                  {formData.topic === 'all_users_dev' && getText(
                    'This notification will be sent to all users in development via the all_users_dev topic.',
                    'سيتم إرسال هذا الإشعار إلى جميع المستخدمين في التطوير عبر موضوع all_users_dev.'
                  )}
                </Alert>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
            <TextField
              label={getText('Title (English)', 'العنوان (الإنجليزية)')}
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
            />

            <TextField
              label={getText('Title (Arabic)', 'العنوان (العربية)')}
              value={formData.titleAr}
              onChange={(e) => setFormData({ ...formData, titleAr: e.target.value })}
              fullWidth
              dir="rtl"
            />

            <TextField
              label={getText('Body (English)', 'النص (الإنجليزية)')}
              value={formData.body}
              onChange={(e) => setFormData({ ...formData, body: e.target.value })}
              fullWidth
              required
              multiline
              rows={4}
            />

            <TextField
              label={getText('Body (Arabic)', 'النص (العربية)')}
              value={formData.bodyAr}
              onChange={(e) => setFormData({ ...formData, bodyAr: e.target.value })}
              fullWidth
              multiline
              rows={4}
              dir="rtl"
            />
          </Box>

          {/* Additional Data */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {getText('Additional Data (Optional)', 'بيانات إضافية (اختياري)')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label={getText('Key', 'المفتاح')}
                value={dataKey}
                onChange={(e) => setDataKey(e.target.value)}
                size="small"
              />
              <TextField
                label={getText('Value', 'القيمة')}
                value={dataValue}
                onChange={(e) => setDataValue(e.target.value)}
                size="small"
              />
              <Button onClick={handleAddData} variant="outlined" size="small">
                {getText('Add', 'إضافة')}
              </Button>
            </Box>
            {Object.keys(formData.data).length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(formData.data).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    onDelete={() => handleRemoveData(key)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            {getText('Cancel', 'إلغاء')}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            disabled={loading}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #6a4190 100%)',
              }
            }}
          >
            {loading ? getText('Sending...', 'جاري الإرسال...') : getText('Send', 'إرسال')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Notifications;

